import { useMemo } from 'react';
import { useProgramConfig } from 'src/hooks/useProgramConfig';

/**
 * The saved fixture sequence is named differently per business: GFC calls it a
 * 'pick path', WFM/Fresh call it a 'product flow'. `productFlowLabel` in the
 * program config holds the lowercase base noun; the UI needs three cased forms:
 *
 *  - `lower`      — mid-sentence: "Cannot save pick path"
 *  - `sentence`   — sentence start: "Product flow saved successfully."
 *  - `title`      — buttons / headings / display-mode names: "Edit Pick Path"
 *
 * Centralized here so casing stays consistent everywhere the label is rendered.
 */
export interface FlowLabel {
  lower: string;
  sentence: string;
  title: string;
}

const capitalizeFirst = (s: string): string => (s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1));

const toTitleCase = (s: string): string => s.split(' ').map(capitalizeFirst).join(' ');

/** Derives the cased forms of the flow label from a lowercase base noun. */
export function buildFlowLabel(base: string): FlowLabel {
  // Fall back to 'product flow' if a program config omits the label, so callers
  // never render "undefined" or crash on casing.
  const safeBase = base || 'product flow';
  return {
    lower: safeBase,
    sentence: capitalizeFirst(safeBase),
    title: toTitleCase(safeBase),
  };
}

/**
 * Returns the current program's flow label in its three cased forms. Memoized
 * on the base noun so the returned object is referentially stable across renders
 * (safe to use in useMemo/useCallback dependency arrays).
 */
export function useFlowLabel(): FlowLabel {
  const config = useProgramConfig();
  const base = config.zoneMapBehavior.productFlowLabel;
  return useMemo(() => buildFlowLabel(base), [base]);
}
